import { Component, type ErrorInfo, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"

type Props = {
  children: ReactNode
  /** Τι να δείξει αντί για το προεπιλεγμένο fallback. */
  fallback?: ReactNode
}

type State = { error: Error | null }

/**
 * Χωρίς error boundary, ένα throw σε οποιοδήποτε render ξεφορτώνει ΟΛΟ το
 * δέντρο του React και ο χρήστης μένει με λευκή σελίδα — χωρίς μενού, χωρίς
 * τρόπο επιστροφής, και με ό,τι είχε συμπληρώσει χαμένο.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Μη χειρισμένο σφάλμα UI:", error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback

    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center px-4">
        <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
        <p className="text-sm font-medium">Παρουσιάστηκε σφάλμα</p>
        <p className="text-xs text-muted-foreground max-w-md text-pretty">
          Κάτι πήγε στραβά σε αυτή τη σελίδα. Δοκιμάστε ξανά — αν επιμείνει, επικοινωνήστε με τον διαχειριστή.
        </p>
        <div className="flex gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => this.setState({ error: null })}>
            Δοκιμή ξανά
          </Button>
          <Button size="sm" onClick={() => window.location.reload()}>
            Ανανέωση σελίδας
          </Button>
        </div>
      </div>
    )
  }
}
